import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './OperatorLogin.css';

/**
 * Operator Login Component
 * Shows logged-in operator or prompts for barcode scan
 * Stores operator in localStorage for persistence
 */
function OperatorLogin({ onOperatorChange, department }) {
  const [operator, setOperator] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanInput, setScanInput] = useState('');
  const [error, setError] = useState('');
  const [operators, setOperators] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const scanInputRef = useRef(null);

  // Load saved operator on mount
  useEffect(() => {
    const savedOperator = localStorage.getItem('currentOperator');
    if (savedOperator) {
      try {
        const parsed = JSON.parse(savedOperator);
        setOperator(parsed);
        if (onOperatorChange) onOperatorChange(parsed);
      } catch (e) {
        localStorage.removeItem('currentOperator');
      }
    }
    fetchOperators();
  }, []);

  // Fetch all operators for dropdown fallback
  const fetchOperators = async () => {
    try {
      const response = await axios.get('/api/operators');
      setOperators(response.data);
    } catch (error) {
      console.error('Error fetching operators:', error);
    }
  };

  // Handle barcode scan
  const handleScan = useCallback(async (barcode) => {
    if (!barcode.trim()) return;

    try {
      const response = await axios.get(`/api/operators/barcode/${barcode.trim()}`);
      const op = response.data;

      setOperator(op);
      localStorage.setItem('currentOperator', JSON.stringify(op));
      localStorage.setItem('username', op.display_name); // For backward compatibility

      if (onOperatorChange) onOperatorChange(op);

      setScanning(false);
      setScanInput('');
      setError('');
    } catch (error) {
      setError('Badge not recognized');
      setScanInput('');
      setTimeout(() => setError(''), 2000);
    }
  }, [onOperatorChange]);

  // Handle manual selection from dropdown
  const handleSelectOperator = (op) => {
    setOperator(op);
    localStorage.setItem('currentOperator', JSON.stringify(op));
    localStorage.setItem('username', op.display_name);

    if (onOperatorChange) onOperatorChange(op);

    setShowDropdown(false);
    setScanning(false);
  };

  // Handle logout
  const handleLogout = () => {
    setOperator(null);
    localStorage.removeItem('currentOperator');
    localStorage.removeItem('username');
    if (onOperatorChange) onOperatorChange(null);
  };

  // Start scanning mode
  const startScanning = () => {
    setScanning(true);
    setError('');
    setTimeout(() => {
      if (scanInputRef.current) {
        scanInputRef.current.focus();
      }
    }, 100);
  };

  // Handle scan input submission
  const handleScanSubmit = (e) => {
    e.preventDefault();
    if (scanInput.trim()) {
      handleScan(scanInput);
    }
  };

  // If operator is logged in, show their name with logout option
  if (operator) {
    return (
      <div className="operator-login logged-in">
        <div className="operator-info">
          <span className="operator-icon">&#9679;</span>
          <span className="operator-name">{operator.display_name}</span>
        </div>
        <button className="logout-btn" onClick={handleLogout}>
          Switch
        </button>
      </div>
    );
  }

  // Show login prompt
  return (
    <div className="operator-login not-logged-in">
      {scanning ? (
        <form onSubmit={handleScanSubmit} className="scan-form">
          <input
            ref={scanInputRef}
            type="text"
            value={scanInput}
            onChange={(e) => setScanInput(e.target.value)}
            placeholder="Scan badge..."
            className="badge-scan-input"
            autoFocus
          />
          {error && <span className="scan-error">{error}</span>}
          <button type="button" className="cancel-btn" onClick={() => setScanning(false)}>
            Cancel
          </button>
        </form>
      ) : (
        <div className="login-prompt">
          <button className="scan-badge-btn" onClick={startScanning}>
            Scan Badge
          </button>
          <div className="dropdown-container">
            <button
              className="select-btn"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              Select
            </button>
            {showDropdown && (
              <div className="operator-dropdown">
                {operators.map(op => (
                  <div
                    key={op.id}
                    className="operator-option"
                    onClick={() => handleSelectOperator(op)}
                  >
                    {op.display_name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default OperatorLogin;
